import { supabase } from '../lib/supabase';
import { emailService } from './emailService';

// Convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj) return obj;
  return {
    id: obj?.id,
    orderNumber: obj?.order_number,
    userId: obj?.user_id,
    orderType: obj?.order_type,
    referenceId: obj?.reference_id,
    status: obj?.status,
    paymentStatus: obj?.payment_status,
    paymentIntentId: obj?.payment_intent_id,
    stripeCustomerId: obj?.stripe_customer_id,
    subtotal: obj?.subtotal,
    taxAmount: obj?.tax_amount,
    discountAmount: obj?.discount_amount,
    totalAmount: obj?.total_amount,
    currency: obj?.currency,
    customerName: obj?.customer_name,
    customerEmail: obj?.customer_email,
    customerPhone: obj?.customer_phone,
    billingAddress: obj?.billing_address,
    shippingAddress: obj?.shipping_address,
    orderItems: obj?.order_items,
    notes: obj?.notes,
    metadata: obj?.metadata,
    createdAt: obj?.created_at,
    updatedAt: obj?.updated_at,
  };
};

// Convert camelCase to snake_case
const toSnakeCase = (obj) => {
  return {
    order_number: obj?.orderNumber,
    user_id: obj?.userId,
    order_type: obj?.orderType,
    reference_id: obj?.referenceId,
    status: obj?.status,
    payment_status: obj?.paymentStatus,
    payment_intent_id: obj?.paymentIntentId,
    stripe_customer_id: obj?.stripeCustomerId,
    subtotal: obj?.subtotal,
    tax_amount: obj?.taxAmount,
    discount_amount: obj?.discountAmount,
    total_amount: obj?.totalAmount,
    currency: obj?.currency,
    customer_name: obj?.customerName,
    customer_email: obj?.customerEmail,
    customer_phone: obj?.customerPhone,
    billing_address: obj?.billingAddress,
    shipping_address: obj?.shippingAddress,
    order_items: obj?.orderItems,
    notes: obj?.notes,
    metadata: obj?.metadata,
  };
};

export const paymentService = {
  async createPaymentIntent(orderData, customerInfo) {
    const { data, error } = await supabase?.functions?.invoke('create-payment-intent', {
      body: {
        orderData,
        customerInfo
      }
    });

    if (error) throw error;
    return data;
  },

  async confirmPayment(paymentIntentId) {
    const { data, error } = await supabase?.functions?.invoke('confirm-payment', {
      body: { paymentIntentId }
    });

    if (error) throw error;
    
    // Send payment receipt email
    if (data?.order) {
      try {
        await emailService?.sendPaymentReceiptEmail(data?.order);
      } catch (emailError) {
        console.error('Failed to send payment receipt email:', emailError);
      }
    }
    
    return data;
  },

  async createOrder(orderData) {
    const { data, error } = await supabase
      ?.from('orders')
      ?.insert(toSnakeCase(orderData))
      ?.select()
      ?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async getOrderById(id) {
    const { data, error } = await supabase
      ?.from('orders')
      ?.select('*')
      ?.eq('id', id)
      ?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async getOrderByPaymentIntent(paymentIntentId) {
    const { data, error } = await supabase
      ?.from('orders')
      ?.select('*')
      ?.eq('payment_intent_id', paymentIntentId)
      ?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async getUserOrders(userId) {
    const { data, error } = await supabase
      ?.from('orders')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    return data?.map(toCamelCase) || [];
  },

  async getAllOrders() {
    const { data, error } = await supabase
      ?.from('orders')
      ?.select('*')
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    return data?.map(toCamelCase) || [];
  },

  async updateOrderStatus(orderId, status, paymentStatus) {
    const updateData = {};
    if (status) updateData.status = status;
    if (paymentStatus) updateData.payment_status = paymentStatus;

    const { data, error } = await supabase
      ?.from('orders')
      ?.update(updateData)
      ?.eq('id', orderId)
      ?.select()
      ?.single();

    if (error) throw error;
    
    const updatedOrder = toCamelCase(data);
    
    // Send payment receipt email if payment is completed
    if (paymentStatus === 'paid') {
      try {
        await emailService?.sendPaymentReceiptEmail(updatedOrder);
      } catch (emailError) {
        console.error('Failed to send payment receipt email:', emailError);
      }
    }
    
    return updatedOrder;
  },

  async createTransaction(transactionData) {
    const { data, error } = await supabase
      ?.from('payment_transactions')
      ?.insert({
        order_id: transactionData?.orderId,
        payment_intent_id: transactionData?.paymentIntentId,
        stripe_charge_id: transactionData?.stripeChargeId,
        amount: transactionData?.amount,
        currency: transactionData?.currency,
        status: transactionData?.status,
        transaction_type: transactionData?.transactionType,
        gateway: transactionData?.gateway || 'stripe',
        gateway_transaction_id: transactionData?.gatewayTransactionId,
        error_message: transactionData?.errorMessage,
        metadata: transactionData?.metadata,
      })
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  async getOrderTransactions(orderId) {
    const { data, error } = await supabase
      ?.from('payment_transactions')
      ?.select('*')
      ?.eq('order_id', orderId)
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  formatAmount(amount, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    })?.format(amount);
  },

  subscribeToOrders(callback) {
    const channel = supabase
      ?.channel('orders')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, (payload) => {
        callback(payload);
      })
      ?.subscribe();

    return () => supabase?.removeChannel(channel);
  },
};